import { ComponentRef, DestroyRef, ViewContainerRef } from '@angular/core';
import {
	IActionCenterLinker,
	IActionConfig,
	IActionHandler,
	ILinkingConfig,
	TargetTypes,
} from '../interfaces';
import { ActionCenterLinkerFactory } from '../actions/linking/factory';
import { ActionCenterService } from '@app/core/services/action-center.service';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableType } from '@app/pages/cmms/types';

function configInputComponent(
	dynamicComponent: ComponentRef<any>,
	tableType: TableType,
	config: IActionConfig
) {
	const dynamicInstance = dynamicComponent.instance;
	dynamicInstance.actionCenter = true;
	if (tableType === TableType.ASSETS && config.name === 'locations') {
		dynamicInstance.disableMultipleSelect = true;
		config.autoEnter = true;
	}
}

export class LinkingActionHandler implements IActionHandler {
	private targets: TargetTypes[] = [];
	private extraConfig: ILinkingConfig;
	private entityConfig: string[] = [
		'assets',
		'work_order',
		'incident',
		'request',
		'files',
	];

	constructor(
		private actionCenterService: ActionCenterService,
		private viewContainerRef: ViewContainerRef,
		private linkerFactory: ActionCenterLinkerFactory,
		private destroyRef: DestroyRef
	) {}

	private openLinkerComponent(config: IActionConfig): ComponentRef<any> {
		const dynamicComponent: ComponentRef<any> = this.viewContainerRef.createComponent(
			this.extraConfig.component
		);
		// Spell to ward off the evil spirits
		if (this.entityConfig.includes(config.name)) {
			const dynamicInstance = dynamicComponent.instance;
			dynamicInstance.activeConfigName = config.name;
			dynamicInstance.editEntityType = this.actionCenterService.tableType.value;
		} else if (config.name === 'locations' || config.name === 'schedule') {
			configInputComponent(
				dynamicComponent,
				this.actionCenterService.tableType.value,
				config
			);
		}
		return dynamicComponent;
	}

	private getTargetSelectionEmitter(
		dynamicComponent: ComponentRef<any>,
		config: IActionConfig
	): Observable<TargetTypes[]> {
		const eventObservable = this.extraConfig.getTargetSelectionEmitter(
			dynamicComponent.instance
		);

		if (!eventObservable) {
			console.error(
				`Could not find the event emitter for ${config.name}, please check "getTargetSelectionEmitter" in actionConfigs and the relevant component`
			);
			return;
		}

		return eventObservable;
	}

	private async triggerAction(linker: IActionCenterLinker<any>): Promise<void> {
		if (!this.targets) {
			return;
		}
		await linker.link(
			this.actionCenterService.selectedEntities.map(entity =>
				entity.pk ? entity.pk : entity
			),
			this.targets
		);
		this.actionCenterService.actionDone$.next();
	}

	handleAction(config: IActionConfig): void {
		this.extraConfig = config as ILinkingConfig;
		if (!this.extraConfig) {
			return;
		}

		const linker = this.linkerFactory.createLinker(
			this.actionCenterService.tableType.value,
			config.name
		);
		const dynamicComponent = this.openLinkerComponent(config);
		const targetsObs = this.getTargetSelectionEmitter(dynamicComponent, config);

		if (!targetsObs) {
			return;
		}

		targetsObs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async targets => {
			this.targets = targets;
			if (config.autoEnter) {
				this.actionCenterService.applyAction$.next();
			}
			if (targets.length) {
				this.actionCenterService.enableApplyButton$.next(true);
				return;
			}
			this.actionCenterService.enableApplyButton$.next(false);
		});

		this.actionCenterService.applyAction$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(async () => {
				if (!this.targets?.length) {
					return;
				}
				this.actionCenterService.closeActionCenter$.next();
				await this.triggerAction(linker);
			});
	}
}
